import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import classNames from 'classnames';

import useNavigateWithSearch from '../../../../../hooks/useNavigateWithSearch';

import { REWARD_ID_ROUTE_PARAM, ROUTE_WALLET, ROUTE_REWARD_INFO, ROUTE_REWARDS, ROUTE_DEALS } from '../../../config/routes';
import { REWARD_AMOUNT_TYPES, REWARD_EXPIRE_AT_TAG } from '../../../config/rewards';
import { getActionType, getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../../config/tracker';
import { formatFullDateUTC, isSoonDate } from '../../../utils/dates';
import { getRewardOptionSubtitle, isDonationType, isFundsErrorCode, isLimitErrorCode, isUserLimitErrorCode, replaceRewardTags, getTypeText, isGiveAwayType } from '../../../utils/rewards';
import { BUTTON_SIZE, BUTTON_TYPE, Button } from '../../../global/Button';
import { Flag } from '../../../global/Rewards';
import TermsDisclaimer from '../../../global/TermsDisclaimer/TermsDisclaimer';
import CompoundRadioSelector, { OPTION_STATUS } from '../../../global/CompoundRadioSelector';
import Advice from '../Advice';
import RewardSummary from './RewardSummary';
import SliderSelector from '../SliderSelector';
import TrashieCoin from '../../../images/trashie-cash-v3.svg';
import {
  TAKE_BACK_BAG_COLLECTION_URL,
} from '../../../config/externalURL';
import LoadingBar from '../../../global/LoadingBar';

import './RewardContent.scss';

const {
  rewards: {
    singleMerchantPage: {
      rewardItem: trackerRewardItem,
    },
  },
} = TRACKER_IDS;

const DEALS_BATCH_SIZE = 5;

const RewardContentMerchantMain = ({
  rewards,
  rewardItem,
  rewardsAllStatus,
  selectorTitle,
  submitDisabled,
  onSelect,
  onSubmit,
  onShowTerms,
  isDiscountCode,
  currentBalance,
  isDeal,
  rewardProperties,
  appliesMultiplier,
  trackerProps = {},
}) => {
  const [seeMoreHideText, setSeeMoreHideText] = useState(true);
  const [visibleRewardsCount, setVisibleRewardsCount] = useState(DEALS_BATCH_SIZE);
  const [isLoadingMoreDeals, setIsLoadingMoreDeals] = useState(false);
  const loadMoreTimeoutRef = useRef(null);
  const navigateWithSearch = useNavigateWithSearch();
  const isGiveAway = isGiveAwayType(rewardItem?.type);

  const {
    uid: rewardId,
    terms = '',
    campaignEndDate,
    expireAt,
    disabled: statusDisabledReward,
    affiliateLink,
    type: rewardType,
    allowTcCredits,
    store: {
      imgUrl,
      description: storeDescription,
      logoUrl,
      RewardLogoProperties,
      url,
      allowTcCredits: storeAllowTcCredits,
      tcCreditAmount,
      tcCreditAmountType,
    },
  } = rewardItem;

  useEffect(() => {
    setVisibleRewardsCount((prevVisible) => {
      if (!rewards.length) {
        return DEALS_BATCH_SIZE;
      }

      const minimumVisible = Math.min(DEALS_BATCH_SIZE, rewards.length);

      if (prevVisible > rewards.length) {
        return rewards.length;
      }

      return Math.max(prevVisible, minimumVisible);
    });
    setIsLoadingMoreDeals(false);
  }, [rewards.length]);

  useEffect(() => () => {
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current);
    }
  }, []);

  const visibleRewards = useMemo(
    () => rewards.slice(0, visibleRewardsCount),
    [rewards, visibleRewardsCount],
  );

  const selectorSectionsMain = useMemo(() => [
    {
      id: 'defaultSection',
      title: '',
      iconTitle: '',
      options: visibleRewards
        .filter(({ disabled, status }) => !(disabled && isUserLimitErrorCode(status)))
        .map(({
          uid,
          name,
          amountType,
          rewardAmount,
          type,
          exchangeValue,
          description,
          status,
          disabled,
          tooltipDisabled,
        }) => ({
          id: uid,
          title: REWARD_AMOUNT_TYPES[amountType]?.getText({ rewardAmount, type }) ?? '',
          subtitle: getRewardOptionSubtitle(status, exchangeValue) === 'FREE!'
            || getRewardOptionSubtitle(status, exchangeValue) === 'SOLD OUT' ?
            getRewardOptionSubtitle(status, exchangeValue) : (
              <>
                <img src={TrashieCoin} className="tcIcon" alt="trashie cash icon" />
                {getRewardOptionSubtitle(status, exchangeValue)}
              </>
            ),
          description: disabled && isFundsErrorCode(status) ? (
            <>
              <p>{description}</p>
              <p>You need more TrashieCash™</p>
            </>
          ) : description,
          status: !disabled ? OPTION_STATUS.AVAILABLE : (
            (isLimitErrorCode(status) && OPTION_STATUS.FORBIDDEN) ||
            (isUserLimitErrorCode(status) && OPTION_STATUS.CHECKED) ||
            OPTION_STATUS.LOCKED
          ),
          tooltipDisabled,
          trackerProps: {
            [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerRewardItem),
            [POSTHOG_CAPTURE_ATTRIBUTES.ACTION_ID]: getActionType(trackerRewardItem),
            [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_ID]: uid,
            [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_NAME]: name,
          },
        })),
    },
  ], [visibleRewards]);

  const hasMoreDealsToShow = useMemo(
    () => visibleRewards.length < rewards.length,
    [visibleRewards.length, rewards.length],
  );

  const handleShowMoreDeals = useCallback(() => {
    if (!hasMoreDealsToShow || isLoadingMoreDeals) {
      return;
    }
    setIsLoadingMoreDeals(true);
    loadMoreTimeoutRef.current = setTimeout(() => {
      setVisibleRewardsCount((prev) => Math.min(prev + DEALS_BATCH_SIZE, rewards.length));
      setIsLoadingMoreDeals(false);
    }, 400);
  }, [hasMoreDealsToShow, isLoadingMoreDeals, rewards.length]);

  const typeText = useMemo(() => getTypeText({
    isDonation: isDonationType(rewardType),
    isDeal,
    isGiveAway,
  }), [isDonationType, isDeal, isGiveAway, rewardType]);

  const buttonLabelSelector = useCallback(() => {
    if (submitDisabled && isDeal) {
      return 'GET YOUR FIRST TAKE BACK BAG';
    }

    if (submitDisabled) {
      return `Browse all ${typeText.toLowerCase()}s`;
    }

    return `GET THIS ${typeText}`;
  }, [submitDisabled, submitDisabled, isDeal, typeText]);

  const handleStickyOnClick = useCallback(() => {
    if (statusDisabledReward && isDeal) {
      return () => { window.location.href = TAKE_BACK_BAG_COLLECTION_URL; };
    }

    if (statusDisabledReward) {
      return () => { navigateWithSearch(isDeal ? ROUTE_DEALS : ROUTE_REWARDS); };
    }

    return onSubmit;
  }, [statusDisabledReward, isDeal, navigateWithSearch]);

  const selectorSectionsClaimed = useMemo(() => {
    const redeemedCodesOptions = rewards
      .filter(({ rewardCodes }) => rewardCodes?.length)
      .map(({
        amountType,
        type,
        rewardAmount,
        description,
        rewardCodes,
      }) => (
        rewardCodes.map(({ uid: rewardCodeid }) => ({
          id: rewardCodeid,
          title: REWARD_AMOUNT_TYPES[amountType]?.getText({ rewardAmount, type }) ?? '',
          subtitle: (
            <Link
              to={ROUTE_REWARD_INFO.replace(REWARD_ID_ROUTE_PARAM, rewardCodeid)}
            >
              VIEW DETAILS
            </Link>
          ),
          description,
          status: OPTION_STATUS.USED,
        }))
      ))
      .flat(Infinity);

    const sections = [];

    if (redeemedCodesOptions.length) {
      sections.push({
        id: 'MyWalletSection',
        title: '',
        iconTitle: '',
        options: redeemedCodesOptions.splice(0, 5),
        extraContent: (
          <Link to={ROUTE_WALLET}>
            {`VIEW ALL CLAIMED ${typeText}S`}
          </Link>
        ),
      });
    }

    return sections;
  }, [rewards, typeText]);

  return (
    <div className="body-style merchant_main">
      <div className="merchant-info">
        <a href={affiliateLink ?? url}>
          <div
            className="merchant-image"
            alt="merchant image"
            style={{
              backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%),url(${imgUrl})`,
            }}
          >
            <Flag
              status={rewardsAllStatus}
              properties={rewardProperties}
              allowTcCredits={allowTcCredits || storeAllowTcCredits}
              tcCreditAmount={tcCreditAmount}
              tcCreditAmountType={tcCreditAmountType}
              expireAt={campaignEndDate}
              appliesMultiplier={appliesMultiplier}
            />
            <img
              className="merchant-logo"
              src={logoUrl}
              style={{ ...RewardLogoProperties }}
              alt="merchant logo"
            />
          </div>

        </a>
        <div className="description-container">
          <div className={seeMoreHideText && 'description-hidden'}>
            <Markdown className={classNames('markdown', { 'description-hidden': seeMoreHideText })}>
              {storeDescription}
            </Markdown>
          </div>
          {storeDescription && (
            <div className="see-more" onClick={() => setSeeMoreHideText(!seeMoreHideText)} aria-hidden>
              {seeMoreHideText ? 'more' : 'less'}
            </div>
          )}
        </div>
      </div>
      <div className="rewardInfo">
        {rewards.lenght === 0 ? (
          <p className="title">NO REWARDS AVAILABLE FOR THIS MERCHANT</p>
        ) : (
          <>
            <div className="action-container">
              {isDiscountCode ? (
                <SliderSelector
                  value={rewardItem.uid}
                  title={selectorTitle}
                  options={rewards.filter(({ disabled, status }) => (
                    !(disabled && isUserLimitErrorCode(status))
                  ))}
                  onChange={(optionId) => onSelect(optionId)}
                  currentBalance={currentBalance}
                />
              ) : (
                selectorSectionsMain[0]?.options?.length > 0 && (
                  <>
                    <CompoundRadioSelector
                      name="main"
                      value={rewardItem.uid}
                      title={selectorTitle}
                      sections={selectorSectionsMain}
                      onChange={(optionId) => onSelect(optionId)}
                    />
                    {(hasMoreDealsToShow || isLoadingMoreDeals) && (
                      <div className="load-more-deals">
                        {isLoadingMoreDeals ? (
                          <LoadingBar />
                        ) : (
                          <Button
                            type={BUTTON_TYPE.SECONDARY}
                            size={BUTTON_SIZE.MEDIUM}
                            onClick={handleShowMoreDeals}
                          >
                            SHOW MORE DEALS
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )
              )}
              {!isDiscountCode && selectorSectionsClaimed[0]?.options?.length > 0 && (
                <CompoundRadioSelector
                  name="claimed"
                  title="CLAIMED BY YOU"
                  sections={selectorSectionsClaimed}
                />
              )}
              <div className="confirm-button-container">
                <Button
                  className="confirm-button"
                  type={statusDisabledReward ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.LARGE}
                  disabled={!submitDisabled && statusDisabledReward}
                  onClick={handleStickyOnClick()}
                  {...{ trackerProps }}
                >
                  {buttonLabelSelector()}
                </Button>
              </div>
            </div>
            {rewardId && (
              <div className="content">
                <RewardSummary
                  rewardItem={rewardItem}
                  title={`${typeText} DETAILS`}
                />
              </div>
            )}
            <TermsDisclaimer
              prefix={(<Advice />)}
              terms={replaceRewardTags(terms, {
                [REWARD_EXPIRE_AT_TAG]:
                  expireAt && isSoonDate(expireAt, 6, 'months') ? formatFullDateUTC(expireAt) : '',
              })}
              ctaText="VIEW ALL TERMS AND INSTRUCTIONS"
              termsTitle="TERMS AND INSTRUCTIONS"
              onHandleClick={onShowTerms}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RewardContentMerchantMain;
